import { Icon } from '@arcadiapower/shrike';
import { useEffect, useState } from 'react';
import { Button } from '~/components/button';
import { DialogSelector, UTILITY_SYNC } from '~/components/dialogs';
import { Month } from '~/components/dropdowns';
import { ErrorMessage } from '~/components/error-message';
import { Expander } from '~/components/expander';
import { Form } from '~/components/form';
import {
  ImageSelector,
  ImageType,
  HALFMOON_SOLAR,
  AMEREN_AVERAGE_YEARLY_KWH,
  AMEREN_METER_USAGE,
  EVERSOURCE_METER_USAGE,
  UNITIL_AVERAGE_YEARLY_KWH,
} from '~/components/image';
import { Inputs } from '~/components/inputs';
import {
  SecondaryIdentifier,
  SecondaryIdFieldMetadata,
} from '~/components/inputs/secondary-identifier/secondary-identifier.component';
import { Container, TwoColumnGrid } from '~/components/layout';
import { Link } from '~/components/link';
import { Description, Title } from '~/components/typography';
import { COMMUNITY_SOLAR_FUNNEL_ID } from '~/funnels/community-solar/config';
import { GO_SOLAR_FUNNEL_ID } from '~/funnels/go-solar/config';
import { useCopy, useForm } from '~/hooks';
import { AccountNumberSubmitFormData } from '~/machines/enrollment-selection';
import { LDClient, LDFlags } from '~/providers';
import { fetchBillingExperienceOptions } from '~/services';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { AddressType, User, Utility, EnrollmentDataSource } from '~/typings/context';
import { UTILITY_IDS } from '~/utils/constants';
import { TRACK, trackError } from '~/utils/tracking';
import { DualBillingModal } from '../modal/dual-billing.component';
import { PlaceHolderForDropdown } from './account-number.style';

const UnitilAnnualUsageHelper = () => (
  <ul>
    <li>
      Please refer to the ‘Electric Usage (kWh)’ graph on your Unitil bill (expand to see
      screenshot) and add up your estimated usage for each month.
    </li>
    <br />

    <li>
      <em>Est. annual usage = Jan usage + Feb usage... + Dec usage</em>
    </li>
    <br />
    <li>
      If fewer than 12 months of usage history are available, add up the provided usage, divide by
      the number of months available, and multiply by 12.
    </li>
    <br />
    <li>
      <em>Est. annual usage = Average monthly usage X 12</em>
    </li>
  </ul>
);

const annualKwhFieldBillExamples = {
  [UTILITY_IDS['Ameren IL']]: {
    type: AMEREN_AVERAGE_YEARLY_KWH,
    title:
      'Please add up your monthly kWh usage. If fewer than 12 months are available, divide by the number of months available and multiply by 12.',
  },
  [UTILITY_IDS['Unitil MA']]: {
    type: UNITIL_AVERAGE_YEARLY_KWH,
    title: <UnitilAnnualUsageHelper />,
  },
};

const meterNumberBillExamples = {
  [UTILITY_IDS['Ameren IL']]: {
    type: AMEREN_METER_USAGE,
    title: 'Find your meter number in your bill',
  },
  [UTILITY_IDS['Eversource / NSTAR']]: {
    type: EVERSOURCE_METER_USAGE,
    title:
      'The 7 digit meter number can be found on the second page of your Eversource NSTAR utility bill',
  },
};

export const AccountNumber = ({
  context,
  error,
  next,
  overrideCopy,
  pending,
  secondaryIdFieldMetadata,
}: AccountNumberProps) => {
  const ffShowMeterNumber = LDClient.variation(LDFlags.ffShowMeterNumber);
  const { unparsedQueryParams } = context.funnelPacket;
  const copy = useCopy(context, defaultCopy, overrideCopy);
  const { state } = context.utility;
  const isComEd = context.utility.id === UTILITY_IDS['Commonwealth Edison'];
  const isAmeren = context.utility.id === UTILITY_IDS['Ameren IL'];
  const isUnitilMA =
    context.utility.id === UTILITY_IDS['Unitil MA'] &&
    [COMMUNITY_SOLAR_FUNNEL_ID, GO_SOLAR_FUNNEL_ID].includes(context.funnelId!);
  const isDominionVA = context.utility.id === UTILITY_IDS['Dominion Energy VA'];
  const isEverSource =
    context.utility.id === UTILITY_IDS['Eversource / NSTAR'] &&
    [COMMUNITY_SOLAR_FUNNEL_ID, GO_SOLAR_FUNNEL_ID].includes(context.funnelId!);

  const [billingExperienceOptions, setBillingExperienceOptions] = useState<any>();
  const shouldShowAnnualKwhDropdown = isAmeren || isUnitilMA;
  const shouldShowMeterNumberDropdown = isAmeren || isEverSource;

  const querybillingExperienceOptions = async () => {
    try {
      const enrollmentOptions: EnrollmentDataSource =
        context.enrollmentOptions && context.enrollmentOptions[0];
      const utilityId: number = context.utility.id;

      const response = await fetchBillingExperienceOptions({
        enrollmentDataSourceName: enrollmentOptions,
        utilityId,
      });
      setBillingExperienceOptions(response?.billingExperienceOptions);
    } catch (error) {
      trackError(error);
    }
  };

  useEffect(() => {
    querybillingExperienceOptions();
  }, []);

  const initialValue: AccountNumberForm = {
    utilityAccountNumber:
      context.utilityAccountNumber! || unparsedQueryParams?.utilityAccountNumber!,
    address: context.address!,
    addressSecondary: context.addressSecondary,
    ...((isComEd || isAmeren || isUnitilMA) && {
      estimatedAnnualKwh: context.estimatedDailyKwh || unparsedQueryParams?.annualizedUsageEstimate,
    }),
    ...(ffShowMeterNumber && {
      meterNumber: context.meterNumber || unparsedQueryParams?.meterNumber!,
    }),
  };

  let secondaryFieldMetadata: SecondaryIdFieldMetadata | null = null;
  let annualKwhFieldMetadata: SecondaryIdFieldMetadata | null = null;
  if (secondaryIdFieldMetadata && secondaryIdFieldMetadata?.length !== 0) {
    for (const identifierMetadata of secondaryIdFieldMetadata!) {
      const { name } = identifierMetadata;
      switch (name) {
        case 'secondaryIdentifier': {
          initialValue[name] =
            context.secondaryIdentifier || unparsedQueryParams?.secondaryAccountId!;
          secondaryFieldMetadata = identifierMetadata;
          break;
        }
        case 'annualizedUsageEstimate': {
          initialValue.estimatedAnnualKwh =
            context.estimatedAnnualKwh || unparsedQueryParams?.annualizedUsageEstimate!;
          annualKwhFieldMetadata = identifierMetadata;
          break;
        }
        default: {
          initialValue.secondaryIdentifier =
            context.secondaryIdentifier || unparsedQueryParams?.secondaryAccountId!;
          secondaryFieldMetadata = identifierMetadata;
          break;
        }
      }
    }
  }

  const [form, updateForm, isValid] = useForm(initialValue);
  const handleSubmit = () => {
    const submitForm: AccountNumberSubmitFormData = {
      ...form,
      estimatedAnnualKwh:
        isAmeren || isUnitilMA
          ? form.estimatedAnnualKwh || form.estimatedDailyKwh! * 365
          : form.estimatedAnnualKwh,
      utilityAccountNumber: isDominionVA
        ? form.utilityAccountNumber.padStart(12, '0')
        : form.utilityAccountNumber,
    };

    next(submitForm);
  };

  const [showExplainerModal, setShowExplainerModal] = useState<boolean>(false);
  const [hideExplainerModal, setHideExplainerModal] = useState<boolean>(false);

  useEffect(() => {
    if (
      unparsedQueryParams?.utilityAccountNumber ||
      unparsedQueryParams?.secondaryAccountId ||
      context.hideDualBillExplainer
    ) {
      setHideExplainerModal(true);
    }
  }, [context]);

  useEffect(() => {
    if (
      billingExperienceOptions &&
      billingExperienceOptions.length &&
      billingExperienceOptions[0] !== 'ucb' &&
      LDClient.variation(LDFlags.showDualBillExplainer)
    )
      setShowExplainerModal(true);
  }, [billingExperienceOptions]);

  return (
    <TwoColumnGrid testId="screen-AccountNumber">
      {!hideExplainerModal && showExplainerModal && (
        <DualBillingModal
          context={context}
          hide={() => {
            setShowExplainerModal(false);
          }}
        />
      )}
      <TwoColumnGrid.Primary>
        <Title>{copy.title}</Title>
        <Description>{copy.description}</Description>

        {!hideExplainerModal && (
          <Description mb={6}>
            {copy.learnMoreModal && (
              <Icon icon="QuestionCircleInverted" margin={{ bottom: '2px', right: '8px' }} />
            )}
            <DialogSelector
              type={copy.learnMoreModal}
              target={
                <Link color="primary" aria-label="Utility dialog" track={TRACK.learnMoreCsClicked}>
                  {copy.learnMoreText}
                </Link>
              }
            />
          </Description>
        )}

        <Form isValid={isValid} onSubmit={handleSubmit}>
          <Inputs.UtilityAccountNumber
            mb={5}
            onChange={updateForm}
            utilityId={context.utility.id}
            value={form.utilityAccountNumber}
          />

          {'secondaryIdentifier' in form || secondaryFieldMetadata?.optional === true ? (
            <SecondaryIdentifier
              mb={5}
              onChange={updateForm}
              // eslint-disable-next-line dot-notation
              value={form[secondaryFieldMetadata?.name!]}
              fieldMetadata={secondaryFieldMetadata!}
              optional={Boolean(secondaryFieldMetadata?.optional)}
            />
          ) : null}
          <Inputs.Address mb={5} onChange={updateForm} state={state} />
          <Inputs.AddressSecondary mb={5} onChange={updateForm} value={form.addressSecondary} />

          {'month' in form && (
            <Month
              onChange={updateForm}
              value={form.month}
              label="Select a month to enter energy usage for"
              requirements={
                'Select one month from the "usage history" table on your most recent utility bill'
              }
            />
          )}

          {'estimatedAnnualKwh' in form && (
            <Inputs.EstimatedAnnualKwh
              mb={5}
              onChange={updateForm}
              requirements={annualKwhFieldMetadata?.helpText}
              validations={annualKwhFieldMetadata?.validations!}
              label={annualKwhFieldMetadata?.label}
              value={form.estimatedAnnualKwh?.toString()}
            />
          )}
          {'estimatedDailyKwh' in form && (
            <Inputs.EstimatedDailyKwh
              mb={2}
              onChange={updateForm}
              value={form.estimatedDailyKwh?.toString()}
            />
          )}
          {shouldShowAnnualKwhDropdown ? (
            <Expander
              info={annualKwhFieldBillExamples[context.utility.id]}
              utility={context.utility}
              mb={5}
            />
          ) : (
            <PlaceHolderForDropdown />
          )}
          {'estimatedMonthlyKwh' in form && (
            <Inputs.EstimatedMonthlyKwh
              mb={5}
              onChange={updateForm}
              requirements={
                'Enter kWh for the selected month from the "usage history" table on your utility bill'
              }
              value={form.estimatedMonthlyKwh?.toString()}
            />
          )}
          {'meterNumber' in form && (
            <Inputs.MeterNumber
              mb={2}
              utilityId={context.utility.id}
              onChange={updateForm}
              value={form.meterNumber}
            />
          )}
          {shouldShowMeterNumberDropdown ? (
            <Expander
              info={meterNumberBillExamples[context.utility.id]}
              utility={context.utility}
              mb={5}
            />
          ) : (
            <PlaceHolderForDropdown />
          )}
          <Container mt={6}>
            <ErrorMessage mb={2} error={error} />
            <Button mode="primary" disabled={!isValid} loading={pending} type="submit">
              {copy.next}
            </Button>
          </Container>
        </Form>
      </TwoColumnGrid.Primary>
      <TwoColumnGrid.Secondary>
        <ImageSelector type={copy.imageType} />
      </TwoColumnGrid.Secondary>
    </TwoColumnGrid>
  );
};

export const defaultCopy = (context: AccountNumberContext) => ({
  title: 'Next, add your utility account details.',
  description: `You'll still be a ${context.utility.displayName} customer. By providing your account details, you authorize us to obtain your historical energy usage and otherwise verify information necessary to enroll you in community solar.`,
  imageType: HALFMOON_SOLAR as ImageType,
  next: 'Submit',
  utilitySyncCTA: 'Enter utility login credentials',
  learnMoreModal: UTILITY_SYNC,
  learnMoreText: 'Why does Arcadia need to integrate with your utility?',
});

export type AccountNumberCopy = ReturnType<typeof defaultCopy>;

export interface AccountNumberProps {
  context: AccountNumberContext;
  error?: string;
  next: (payload: AccountNumberSubmitFormData) => void;
  overrideCopy?: (context: AccountNumberContext) => Partial<AccountNumberCopy>;
  pending: boolean;
  secondaryIdFieldMetadata?: Array<SecondaryIdFieldMetadata>;
}

export interface AccountNumberForm {
  address: AddressType;
  addressSecondary?: string;
  estimatedAnnualKwh?: number;
  estimatedDailyKwh?: number;
  estimatedMonthlyKwh?: number;
  month?: string;
  meterNumber?: string;
  utilityAccountNumber: string;
  secondaryIdentifier?: string;
}

export interface AccountNumberContext {
  address?: AddressType;
  addressSecondary?: string;
  estimatedAnnualKwh?: number;
  estimatedDailyKwh?: any;
  estimatedMonthlyKwh?: number;
  month?: string;
  meterNumber?: string;
  utility: Utility;
  hideDualBillExplainer?: boolean;
  utilityAccountNumber?: string;
  secondaryIdentifier?: string;
  user: User;
  enrollmentOptions: EnrollmentDataSource[];
  funnelPacket: FetchFunnelPacketResponse;
  funnelId?: string;
  promoCode?: string;
}
